import React, { Component } from "react";
import PropTypes from "prop-types";
import { Switch, Route, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import styled from "styled-components";

import {
  getAuth,
  getAccount,
  getSubscriptionPlans,
  getOrder,
} from "../configureStore";
import { fetchSubscriptionPlans } from "../configureStore/subscriptionPlans";
import { menuOffset, mobileMenuOffset, colors, gradients } from "../theme";
import Account from "../components/support/SupportAccount";
import Informations from "../components/support/SupportInformations";
import Loader from "../components/Loader";
import Error from "../components/Error";
import Payment from "../components/support/SupportPayment";

const HeaderWrapper = styled.div`
  height: 73px;

  @media (max-width: 767px) {
    height: 71px;
  }
`;

const Header = styled.header`
  background: white;
  box-shadow: 0 3px 8px rgba(46, 68, 82, 0.25);
  position: fixed;
  text-align: center;
  top: ${menuOffset};
  width: 100%;
  z-index: 9999;

  @media (max-width: 767px) {
    padding-top: 13px;
    top: ${mobileMenuOffset};
  }

  h1 {
    color: ${colors.gray};
    font-size: 2.4rem;
    font-weight: 700;
    line-height: 2.5rem;
    text-transform: uppercase;

    @media (max-width: 767px) {
      font-size: 1.5rem;
      line-height: 1.6rem;
    }
  }
`;

const Stepper = styled.div`
  display: flex;
  justify-content: center;
  margin: auto;
  position: relative;
  text-align: center;
  width: 520px;

  :after {
    background: ${gradients.purpleToPink()};
    bottom: 0;
    content: "";
    display: block;
    height: 4px;
    left: 0;
    position: absolute;
    transition: 0.3s;
    width: calc(25% * ${(props) => props.step});
  }

  div {
    color: ${colors.lightTextColor};
    font-size: 1.3rem;
    height: 48px;
    line-height: 4.8rem;
    overflow: hidden;
    position: relative;
    text-transform: uppercase;
    width: 130px;

    @media (max-width: 767px) {
      font-size: 0.98rem;
      height: 42px;
      line-height: 4.2rem;
      width: calc(100% / 4);
    }

    &.done {
      font-weight: 700;
    }

    span {
      font-weight: 100;
    }
  }
`;

class SupportPath extends Component {
  static fetchInitialData() {
    return [fetchSubscriptionPlans()];
  }

  componentDidMount() {
    this.fetchContent();
  }

  componentDidUpdate() {
    this.fetchContent();
  }

  fetchContent() {
    const { subscriptionPlans, dispatch } = this.props;

    if (
      !subscriptionPlans.isFetching &&
      !subscriptionPlans.error &&
      !subscriptionPlans.list
    ) {
      dispatch(fetchSubscriptionPlans());
    }
  }

  getStep() {
    const pathname = this.props.location.pathname;
    if (/^\/soutien\/(abonnement|socio)$/.test(pathname)) return 1;
    if (/^\/soutien\/(abonnement|socio)\/coordonnees$/.test(pathname)) return 2;
    if (/^\/soutien\/(abonnement|socio)\/paiement$/.test(pathname)) return 3;
    if (/^\/soutien\/(abonnement|socio)\/bienvenue$/.test(pathname)) return 4;
  }

  shouldGoToPaymentStep() {
    if (!this.props.auth.isConnected) {
      return false;
    }

    const { firstname, lastname, address_billing } = this.props.account;
    if (!address_billing) {
      return false;
    }

    const { street, postal_code, locality, country_name } = address_billing;
    if (
      !firstname ||
      !lastname ||
      !street ||
      !postal_code ||
      !locality ||
      !country_name
    ) {
      return false;
    }

    if (/^socio|parts-sociales$/.test(this.props.match.params.path)) {
      const { gender, birthday, birth_locality, birth_country_name } =
        this.props.account;
      if (!gender || !birthday || !birth_locality || !birth_country_name) {
        return false;
      }
    }

    return true;
  }

  render() {
    const path = this.props.match.params.path;
    const isSocioPath = path === "socio";
    const isSharesPath = path === "parts-sociales";
    const step = this.getStep();

    if (
      (!this.props.account.active_subscription && isSharesPath) ||
      (this.props.account.role === "SUBSCRIBER" && !isSocioPath)
    ) {
      return <Redirect to="/soutien/socio" />;
    }

    if (
      this.props.account.active_subscription &&
      this.props.account.role === "SOCIO" &&
      this.shouldGoToPaymentStep() &&
      !isSharesPath
    ) {
      return <Redirect to="/soutien/parts-sociales" />;
    }

    return (
      <>
        {!isSharesPath && (
          <HeaderWrapper>
            <Header>
              <h1>
                {isSocioPath
                  ? this.props.account.shares_owned &&
                    this.props.account.shares_owned.LEMEDIATV_SHARES_SCIC >= 5
                    ? "Redevenir Socio"
                    : "Devenir Socio"
                  : "S'abonner"}
              </h1>
              <Stepper step={step}>
                <div className="done">
                  <span>1.</span> S'enregistrer
                </div>
                <div className={step >= 2 ? "done" : ""}>
                  <span>2.</span> Coordonnées
                </div>
                <div className={step >= 3 ? "done" : ""}>
                  <span>3.</span> {isSocioPath ? "Souscription" : "Abonnement"}
                </div>
                <div className={step >= 4 ? "done" : ""}>
                  <span>4.</span> Récapitulatif
                </div>
              </Stepper>
            </Header>
          </HeaderWrapper>
        )}
        <Switch>
          <Route
            path="/soutien/:path(abonnement|socio)"
            exact
            render={() => {
              if (this.shouldGoToPaymentStep()) {
                return <Redirect to={`/soutien/${path}/paiement`} />;
              }

              if (this.props.auth.isConnected) {
                return <Redirect to={`/soutien/${path}/coordonnees`} />;
              }

              return <Account {...this.props} />;
            }}
          />
          <Route
            path="/soutien/:path(abonnement|socio)/coordonnees"
            render={() => {
              if (!this.props.auth.isConnected) {
                return <Redirect to={`/soutien/${path}`} />;
              }

              if (this.shouldGoToPaymentStep()) {
                return <Redirect to={`/soutien/${path}/paiement`} />;
              }

              return <Informations {...this.props} />;
            }}
          />
          <Route
            path="/soutien/:path(abonnement|socio)/paiement"
            render={() => {
              if (!this.props.auth.isConnected) {
                return <Redirect to={`/soutien/${path}`} />;
              }

              if (!this.shouldGoToPaymentStep()) {
                return <Redirect to={`/soutien/${path}/coordonnees`} />;
              }

              if (this.props.subscriptionPlans.error) {
                return <Error message={this.props.subscriptionPlans.error} />;
              }

              if (this.props.subscriptionPlans.list) {
                return <Payment {...this.props} />;
              }

              return (
                <div style={{ padding: "80px 0" }}>
                  <Loader />
                </div>
              );
            }}
          />
          <Route
            path="/soutien/parts-sociales"
            render={() => {
              if (
                !this.props.auth.isConnected ||
                !this.shouldGoToPaymentStep()
              ) {
                return <Redirect to={`/soutien/socio`} />;
              }

              if (this.props.subscriptionPlans.list) {
                return <Payment {...this.props} />;
              }

              return (
                <div style={{ padding: "80px 0" }}>
                  <Loader />
                </div>
              );
            }}
          />
          <Redirect to="/soutien" />
        </Switch>
      </>
    );
  }
}

SupportPath.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      path: PropTypes.oneOf(["abonnement", "socio", "parts-sociales"])
        .isRequired,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  auth: PropTypes.shape({
    isConnected: PropTypes.bool.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    address_billing: PropTypes.shape({
      street: PropTypes.string,
      postal_code: PropTypes.string,
      locality: PropTypes.string,
      country_name: PropTypes.string,
    }),
    gender: PropTypes.string,
    birthday: PropTypes.string,
    birth_locality: PropTypes.string,
    birth_country_name: PropTypes.string,
    role: PropTypes.string,
    active_subscription: PropTypes.object,
    shares_owned: PropTypes.shape({
      LEMEDIATV_SHARES_SCIC: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  subscriptionPlans: PropTypes.shape({
    list: PropTypes.array,
    error: PropTypes.string,
    isFetching: PropTypes.bool.isRequired,
  }).isRequired,
};

export default connect((state) => ({
  auth: getAuth(state),
  account: getAccount(state),
  subscriptionPlans: getSubscriptionPlans(state),
  order: getOrder(state),
}))(SupportPath);
